import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';

// Configuration constants for better maintainability
const TOP_NAV_CONFIG = {
  // Margin top values for different page types and breakpoints
  MARGIN_TOP: {
    desktop: {
      withBannerAndTopNav: '150px',
      withOnlyTopNav: '70px',
      hasTopBanner: '100px',
      isPagesWithOnlyTopNavAndFill: '68px',
      default: 0,
    },
    mobile: {
      withBannerAndTopNav: '150px',
      withOnlyTopNav: '56px',
      hasTopBanner: '56px',
      isPagesWithOnlyTopNavAndFill: '56px',
      default: 0,
    },
  },

  // Top position values for banner scenarios
  TOP_POSITION: {
    withBanner: '50px',
    withOfferingBanner: '82px',
    default: 0,
  },

  // Common styles
  COMMON_STYLES: {
    maxWidth: '1180px !important',
    paddingRight: '30px',
    paperBackground: 'rgba(239, 239, 239, 0.8)',
    backdropFilter: 'blur(100px)',
    offeringBorder: 'rgba(119, 119, 119, 0.05) solid 1.52px',
  },
};

// Props that should not be forwarded to the DOM
const FILTERED_PROPS = [
  'hasTopBanner',
  'withBannerAndTopNav',
  'withOnlyTopNav',
  'isPagesWithOnlyTopNavAndFill',
  'withExtendedTopNav',
  'showBannerOnExplorePage',
  'isDefaultBanner',
  'noStrapiDataAtAll',
  'useNewTopNav',
];

// Utility function to filter props
const shouldForwardProp = (prop) => !FILTERED_PROPS.includes(prop);

/**
 * Determines the priority order for margin top calculation
 * Higher priority conditions are checked first
 */
const getMarginTopPriority = () => [
  'withBannerAndTopNav',
  'withOnlyTopNav',
  'hasTopBanner',
  'isPagesWithOnlyTopNavAndFill',
];

/**
 * Calculate margin top based on page configuration and breakpoint
 * @param {Object} config - Configuration object with boolean flags
 * @param {'desktop'|'mobile'} breakpoint - Target breakpoint
 * @returns {string|number} Margin top value
 */
const calculateMarginTop = (config, breakpoint) => {
  const priorities = getMarginTopPriority();
  const marginConfig = TOP_NAV_CONFIG.MARGIN_TOP[breakpoint];

  // Special case: When no Strapi data is present at all, position TopNav flush with default banner
  if (config.noStrapiDataAtAll && config.isDefaultBanner) {
    return '56px'; // Height of the PromoBarBox banner
  }

  // Special case: Explore page with banner should use hasTopBanner margin instead of withOnlyTopNav
  if (config.showBannerOnExplorePage && config.hasTopBanner) {
    return marginConfig.hasTopBanner;
  }

  // Check conditions in priority order
  const matchedCondition = priorities.find(
    (condition) => config[condition] && marginConfig[condition] !== undefined,
  );

  if (matchedCondition) {
    return marginConfig[matchedCondition];
  }

  return marginConfig.default;
};

/**
 * Generate conditional styles for banner scenarios
 * Positions the fixed TopNav below the promo banner when present
 * @param {Object} config - Configuration object with boolean flags
 * @returns {Object} Conditional styles object
 */
const getBannerConditionalStyles = (config) => {
  // Apply top positioning when there's any top banner (homepage, explore, or default)
  // This ensures the fixed TopNav appears below the promo banner
  const shouldApplyBannerStyles =
    config.hasTopBanner || (config.noStrapiDataAtAll && config.isDefaultBanner);

  if (!shouldApplyBannerStyles) {
    return {};
  }

  return {
    '.MuiAppBar-positionFixed': {
      top: '56px !important', // Height of the PromoBarBox banner
    },
  };
};

/**
 * Helper function to determine page type configuration
 * @param {string} pathname - Current page pathname
 * @param {Object} searchParams - URL search parameters
 * @param {Object} offeringData - Offering data if applicable
 * @returns {Object} Configuration object with boolean flags
 */
export const getPageTypeConfig = (pathname, searchParams, offeringData) => {
  const category = searchParams?.get?.('category');
  const search = searchParams?.get?.('search');
  const startups = searchParams?.get?.('startups');
  const preview = searchParams?.get?.('preview');

  // Page type arrays (could be moved to constants if needed elsewhere)
  const pagesWithExtendedTopNav = [];
  const pagesWithBannerAndTopNav = [
    '/ira-account',
    '/terms',
    '/reg-bi-disclosure',
    '/privacy',
    '/marketplace',
    '/annual-reports',
    '/partner',
    '/ira',
    '/form-crs',
    '/equity-trust',
    '/disclaimer',
    '/unfollow',
    '/raise-capital-ai',
    '/raise-capital-ai/thank-you',
    '/raise-capital-ai/submitted',
    '/explore',
    '/contact-us',
    '/seedinvest',
    '/success-stories',
    // /about lives in this list for parity with the other static content
    // pages. The /offering/about-preview entries match the URL-bar pathname
    // after envoy 301-redirects /about-preview through the catch-all rule.
    // Both can drop once the ALB cutover (PD-34435) makes /about reachable
    // directly from the discover origin.
    '/about',
    '/offering/about-preview',
    '/offering/about-preview/',
  ];

  const pagesWithOnlyTopNav = ['/private'];

  const pagesWithOnlyTopNavAndFill = ['/secure'];

  // /offering/* paths that are NOT actual campaign pages — they're rewrite
  // targets used to dodge the upstream envoy rule that 301-redirects unknown
  // top-level paths to /offering/{slug}. Without this exception they'd all be
  // treated as campaign pages and lose the global TopNav + Footer.
  const nonCampaignOfferingPaths = [
    '/offering/llms.txt',
    '/offering/ira',
    '/offering/about-preview',
    '/offering/about-preview/',
  ];
  const isVinovestRewritePath =
    pathname === '/offering/vinovest' || pathname?.startsWith('/offering/vinovest/');

  const isCampaignPage =
    (pathname?.includes('/offering/') &&
      !nonCampaignOfferingPaths.includes(pathname) &&
      !isVinovestRewritePath) ||
    false;
  const isHomepage = pathname === '/';
  const isExplorePage = pathname === '/explore';

  // Offering banner logic
  const offeringBanner =
    offeringData &&
    ['pending', 'not_submitted', 'hidden', 'closed', 'testing', 'preview'].includes(
      offeringData.status,
    );

  // Page type checks
  const isPagesWithExtendedTopNav =
    (pagesWithExtendedTopNav.includes(pathname) && !category) || search || startups;
  const isPagesWithBannerAndTopNav = pagesWithBannerAndTopNav.includes(pathname);
  const isPagesWithOnlyTopNav = pagesWithOnlyTopNav.includes(pathname);
  const isPagesWithOnlyTopNavAndFill = pagesWithOnlyTopNavAndFill.includes(pathname);

  // Banner logic
  const bannerBool =
    offeringBanner || (isPagesWithBannerAndTopNav && !isPagesWithOnlyTopNav) || preview;

  // Homepage banner logic
  const showBannerOnHomepage = isHomepage;

  // Explore page banner logic
  const allStartups = startups === 'all';
  const mobileSeeAll = false; // Typically false in LayoutWrapper context
  const showBannerOnExplorePage =
    isExplorePage && !allStartups && !mobileSeeAll && !category && !search;

  return {
    isCampaignPage,
    isHomepage,
    isExplorePage,
    hasTopBanner: bannerBool || showBannerOnHomepage || showBannerOnExplorePage,
    withExtendedTopNav: isPagesWithExtendedTopNav,
    withBannerAndTopNav: isPagesWithBannerAndTopNav,
    withOnlyTopNav: isPagesWithOnlyTopNav,
    isPagesWithOnlyTopNavAndFill,
    showBannerOnHomepage,
    showBannerOnExplorePage,
    offeringBanner,
  };
};

/**
 * Main TopNav styled component with responsive margin calculations
 * and consistent styling across different page types
 */
export const TopNavStyle = styled(Box, { shouldForwardProp })(({
  theme,
  hasTopBanner,
  display,
  withBannerAndTopNav,
  withExtendedTopNav,
  withOnlyTopNav,
  isPagesWithOnlyTopNavAndFill,
  showBannerOnExplorePage,
  isDefaultBanner,
  noStrapiDataAtAll,
  useNewTopNav,
}) => {
  const config = {
    withBannerAndTopNav,
    withExtendedTopNav,
    hasTopBanner,
    withOnlyTopNav,
    isPagesWithOnlyTopNavAndFill,
    showBannerOnExplorePage,
    isDefaultBanner,
    noStrapiDataAtAll,
  };

  return {
    marginTop: useNewTopNav ? 0 : calculateMarginTop(config, 'mobile'),
    // V2 homepage uses full-width nav without maxWidth constraint
    ...(useNewTopNav && {
      width: '100%',
      maxWidth: 'none !important',
      paddingRight: 0,
      paddingLeft: 0,
    }),
    [theme.breakpoints.up('lg')]: {
      marginTop: useNewTopNav ? 0 : calculateMarginTop(config, 'desktop'),
      // Only apply maxWidth constraint for non-V2 pages
      ...(useNewTopNav
        ? { maxWidth: 'none !important', paddingRight: 0, paddingLeft: 0 }
        : {
            maxWidth: TOP_NAV_CONFIG.COMMON_STYLES.maxWidth,
            paddingRight: TOP_NAV_CONFIG.COMMON_STYLES.paddingRight,
          }),
    },
    '.MuiPaper-root': {
      display: display || 'initial',
      // Use white background when no Strapi data is present at all AND user is not logged in (default banner scenario)
      background: TOP_NAV_CONFIG.COMMON_STYLES.paperBackground,
      backdropFilter: TOP_NAV_CONFIG.COMMON_STYLES.backdropFilter,
    },
    '.MuiToolbar-root': {
      marginTop: '-2px !important',
      padding: {
        sm: '5px 64px 0',
      },
      // Apply background only on mobile AND only on homepage (when no specific page flags are set)
      ...(!withBannerAndTopNav &&
        !withOnlyTopNav &&
        !isPagesWithOnlyTopNavAndFill &&
        !withExtendedTopNav && {
          [theme.breakpoints.down('sm')]: {
            background: 'rgba(239, 239, 239, 0.8)',
          },
        }),
    },
    '.MuiToolbar-root > div > div': {
      height: '100%',
    },
    '.MuiAutocomplete-popper': {
      backgroundColor: 'white',
    },
    ...(useNewTopNav ? {} : getBannerConditionalStyles(config)),
  };
});

/**
 * Offering page specific TopNav styled component
 * with different banner positioning and styling
 */
export const TopNavStyleOffering = styled(Box, { shouldForwardProp })(
  ({ theme, hasTopBanner, display }) => ({
    '.MuiPaper-root': {
      display: display || 'initial',
      background: hasTopBanner ? 'transparent' : TOP_NAV_CONFIG.COMMON_STYLES.paperBackground,
      border: hasTopBanner ? 'none' : TOP_NAV_CONFIG.COMMON_STYLES.offeringBorder,
      position: 'initial',
      '.MuiToolbar-root': {
        padding: '0 24px',
      },
    },
    '.MuiToolbar-root > div > div': {
      height: '100%',
    },
    '.MuiAutocomplete-popper': {
      backgroundColor: 'white',
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: TOP_NAV_CONFIG.COMMON_STYLES.maxWidth,
      paddingRight: TOP_NAV_CONFIG.COMMON_STYLES.paddingRight,
    },
    marginLeft: 'auto',
    marginRight: 'auto',
  }),
);
